import { fetchMyTeams, fetchTeams, type Team } from "@/api/team";
import { useAuth, useTheme } from "@/app/providers";
import { useTeamStore } from "@/app/store/useTeamStore";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, LogOut, Moon, Settings, Sun } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

function TeamSelector() {
  const { grant } = useAuth();
  const isAdmin = grant === "admin";
  const { selectedTeam, selectedTeamId, defaultTeamId, setSelectedTeam, setDefaultTeamId } = useTeamStore();

  const {
    data: myTeams,
    isLoading: isMyTeamsLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["my-teams"],
    queryFn: fetchMyTeams,
    retry: false,
  });

  // 관리자만 전체 팀을 추가 조회. 실패해도 소속 팀만으로 폴백
  const {
    data: allTeams,
    isLoading: isAllTeamsLoading,
    isError: isAllTeamsError,
  } = useQuery({
    queryKey: ["all-teams"],
    queryFn: fetchTeams,
    enabled: isAdmin,
    retry: false,
  });

  const isLoading = isMyTeamsLoading || isAllTeamsLoading;

  // 소속 팀 먼저, 그 뒤 비소속 팀(관리자만). 두 응답 모두 id 순
  const { memberTeams, memberTeamIds, otherTeams, teams } = useMemo(() => {
    const memberTeams = myTeams ?? [];
    const memberTeamIds = new Set(memberTeams.map((t) => t.id));
    const otherTeams = isAdmin && allTeams ? allTeams.filter((t) => !memberTeamIds.has(t.id)) : [];
    return { memberTeams, memberTeamIds, otherTeams, teams: [...memberTeams, ...otherTeams] };
  }, [isAdmin, myTeams, allTeams]);
  // 관리자에게만 전체 팀이 보이므로 소속 팀에 MyTeam 표기
  const isMyTeam = (team: Team) => isAdmin && memberTeamIds.has(team.id);
  const isOtherTeam = (team: Team) => isAdmin && !memberTeamIds.has(team.id);
  const myTeamBadge = <span className="shrink-0 text-[10px] leading-none text-muted-foreground">MyTeam</span>;

  useEffect(() => {
    if (!isLoading && !isError && teams.length > 0 && !selectedTeam) {
      const targetId = selectedTeamId ?? defaultTeamId;
      if (targetId) {
        const team = teams.find((t) => t.id === targetId);
        if (team) {
          setSelectedTeam(team);
        } else {
          setDefaultTeamId(null);
        }
      }
    }
  }, [isLoading, isError, teams, selectedTeam, selectedTeamId, defaultTeamId, setSelectedTeam, setDefaultTeamId]);

  const triggerLabel = isLoading
    ? "팀 불러오는 중..."
    : isError
      ? "팀 로드 실패"
      : (selectedTeam?.name ?? "팀 선택");

  const renderTeamItem = (team: Team) => (
    <DropdownMenuItem
      key={team.id}
      onClick={() => setSelectedTeam(team)}
      className="flex items-center justify-between"
    >
      <span className="flex min-w-0 items-center gap-1.5">
        <span className={isOtherTeam(team) ? "truncate text-foreground/70" : "truncate"}>
          {team.name}
        </span>
        {isMyTeam(team) && myTeamBadge}
      </span>
      {selectedTeam?.id === team.id && <Check className="h-4 w-4 shrink-0" />}
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={isLoading || isError}>
        <Button
          variant={isAdmin ? "ghost" : "outline"}
          size="sm"
          className="w-44 justify-between data-[state=open]:border data-[state=open]:border-input"
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate">{triggerLabel}</span>
            {selectedTeam && !isLoading && !isError && isMyTeam(selectedTeam) && myTeamBadge}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-44">
        {isLoading ? (
          <DropdownMenuLabel className="text-muted-foreground font-normal">
            팀 목록을 불러오는 중...
          </DropdownMenuLabel>
        ) : isError ? (
          <DropdownMenuLabel className="text-destructive font-normal">
            {error instanceof Error ? error.message : "팀을 불러오지 못했습니다"}
          </DropdownMenuLabel>
        ) : teams.length === 0 ? (
          <DropdownMenuLabel className="text-muted-foreground font-normal">
            팀이 없습니다
          </DropdownMenuLabel>
        ) : (
          <>
            {memberTeams.map(renderTeamItem)}
            {otherTeams.length > 0 && (
              <>
                <DropdownMenuSeparator />
                {otherTeams.map(renderTeamItem)}
              </>
            )}
          </>
        )}
        {!isLoading && !isError && isAllTeamsError && (
          <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
            전체 팀 목록을 불러오지 못했습니다
          </DropdownMenuLabel>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Header() {
  const { user, grant, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const displayName = user?.name ?? user?.email ?? "사용자";
  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  const grantLabel = grant === "admin" ? "관리자" : "일반";
  const grantClass =
    grant === "admin"
      ? "text-xs font-medium text-blue-600 dark:text-blue-400"
      : "text-xs font-medium text-muted-foreground";

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-background/95 px-6 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {/* Left: App name + Team selector */}
      <div className="flex items-center gap-4">
        <TeamSelector />
      </div>

      {/* Right: Theme toggle + User menu */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={toggleTheme}>
          {theme === "light" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2 pl-2 pr-3">
              <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-primary text-primary-foreground text-sm font-medium">
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user?.name ? `${user.name} profile` : "profile"}
                    className="h-full w-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span>{displayName.charAt(0).toUpperCase()}</span>
                )}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <span className={grantClass}>{grantLabel}</span>
                </div>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <Settings className="mr-2 h-4 w-4" />
              설정
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              로그아웃
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
